import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import themeData from "src/utils/theme.json";
import { environment } from "src/config/environment";
import { getThemeName } from "src/config/themeConfig";
import { IBrandingConfig } from "../models/interfaces";

/**
 * Default branding is seeded from the active theme in theme.json so the editor
 * opens showing the live brand, not invented values. KCC has no backend wired
 * yet — "Save" persists the config to this slice (redux-persist keeps it across
 * reloads); the real save endpoint can be swapped into the page later.
 */
const buildDefaultConfig = (): IBrandingConfig => {
	const allColors = themeData.colors as unknown as Record<string, Record<string, string>>;
	const themeColors = allColors[getThemeName()] ?? allColors.Kural;

	return {
		appName: environment.REACT_APP_INSTANCE_NAME || "Kural",
		aiName: "Kural",
		colors: {
			primaryColor: themeColors["primary-color"] ?? "#4cbb96",
			primaryColor2: themeColors["primary-color-2"] ?? "#2D8669",
			secondaryColor: themeColors["secondary-color"] ?? "#112e65",
			secondaryColor2: themeColors["secondary-color-2"] ?? "#5E80B7",
			aiAccentColor: themeColors["ai-accent-color"] ?? "#9C67FF",
		},
		logomarkName: "AskKural.svg",
		fullLogoName: "LeftLogoKural.svg",
		logomarkSrc: "",
		fullLogoSrc: "",
	};
};

export interface BrandingState {
	config: IBrandingConfig;
	/** ISO timestamp of the last save (display only). */
	lastSavedAt: string | null;
}

const initialState: BrandingState = {
	config: buildDefaultConfig(),
	lastSavedAt: null,
};

const brandingSlice = createSlice({
	name: "branding",
	initialState,
	reducers: {
		saveBranding: (state, action: PayloadAction<{ config: IBrandingConfig; savedAt: string }>) => {
			state.config = action.payload.config;
			state.lastSavedAt = action.payload.savedAt;
		},
		resetBranding: () => ({ config: buildDefaultConfig(), lastSavedAt: null }),
	},
});

export const { saveBranding, resetBranding } = brandingSlice.actions;
export default brandingSlice.reducer;
